import {
    BoxAccess,
    Checkbox,
    layout,
    PushButton,
    typography,
    useBoxes,
    usePlatform,
    usePreventDeactivate,
    useStorageItemValue
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {Delivery, DeliveryState, getOccupancyDeliveries, PickupEvent, Tenant} from "./types";
import {defined} from "@variocube/app-common";
import {useLocalization} from "../i18n";


interface PickupProps {
    pickupEvent: PickupEvent;
    onConfirm: (pickupEvent: PickupEvent, partialPickup: boolean, learnNfcCard: boolean) => void;
    onCancel: () => void;
    onReturn?: (delivery: Delivery, event: PickupEvent) => void,
    canCancel?: boolean;
}

export function PickupView({pickupEvent, onConfirm, onCancel, onReturn, canCancel}: PickupProps) {
    const [partialPickup, setPartialPickup] = useState(false);
    const [learnNfcCard, setLearnNfcCard] = useState(false);

    usePreventDeactivate();

    const tenant = useStorageItemValue<Tenant>("tenant");
    const allowPartialPickup = tenant?.allowPartialPickup ?? true;
    const allowNfcCardLearning = tenant?.nfcCardLearning || false;

    const {t} = useLocalization();
    const platform = usePlatform();
    const boxes = useBoxes();
    const pickupBoxes = useMemo(() => boxes.filter(box => pickupEvent.occupancies.find(occupancy => occupancy.boxNumber == box.number)), [boxes, pickupEvent]);

    const deliveries = useMemo(() => pickupEvent.occupancies.flatMap(getOccupancyDeliveries).filter(defined), [pickupEvent]);

    const handleConfirm = useCallback(() => onConfirm(pickupEvent, partialPickup, learnNfcCard), [onConfirm, pickupEvent, partialPickup, learnNfcCard]);
    const handleReturn = useCallback(() => {
        if (onReturn && deliveries && deliveries.length > 0) {
            // use first delivery ???
            onReturn(deliveries[0], pickupEvent);
        }
    }, [deliveries, pickupEvent, onReturn])

    useEffect(() => {
        if(tenant && tenant.nfcCardLearning && deliveries && deliveries.length > 0) {
            const delivery = deliveries[0];
            setLearnNfcCard(delivery.recipients.filter((r) => r.pickupKey).length == 0);
        }else {
            setLearnNfcCard(false);
        }
    }, [deliveries, tenant])

    const isReturnDelivery = useMemo(() => Boolean(deliveries.find(delivery => delivery.delivery.state === DeliveryState.Returned)), [deliveries]);

    const boxNames = pickupBoxes.map(box => box.number).join(", ");

    const boxOpenMessageKey = (pickupBoxes.length > 1 ? "PickupMessagePlural" : "PickupMessage");

    return (
        <BoxAccess
            boxes={pickupBoxes}
            platform={platform}
            onCancel={onCancel}
            onConfirm={handleConfirm}
            timeout
            boxOpenTitle={t("PickupTitle")}
            boxOpenMessage={t(boxOpenMessageKey, {boxNames})}
            confirmTitle={t("PickupConfirmTitle")}
            confirmMessage={t("PickupConfirmMessage", {boxNames})}
            openActions={
                <div>
                    {deliveries.map(delivery => (
                        <div className={typography.bigger} key={delivery.delivery.deliveryId}>
                            <div>{t("DeliveryId")}: {delivery.delivery.deliveryId}</div>
                            {delivery.order &&
                                <div>{t("OrderId")}: {delivery.order.orderId} {delivery.order.description}</div>
                            }
                            {delivery.returnDelivery && (
                                <div>{t("ReturnDelivery")}: {delivery.returnDelivery.description}</div>
                            )}
                        </div>
                    ))}

                    {allowPartialPickup && (
                        <div className={layout.marginYMedium}>
                            <Checkbox checked={partialPickup} onChange={setPartialPickup}>
                                {t("PartialPickup")}
                            </Checkbox>
                        </div>
                    )}
                    {allowNfcCardLearning && (
                        <div className={layout.marginYMedium}>
                            <Checkbox checked={learnNfcCard} onChange={setLearnNfcCard}>
                                {t("LearnPickupKeyTitle")}
                            </Checkbox>
                        </div>
                    )}

                    {onReturn && tenant?.returnDeliveryConfig?.enabled && !isReturnDelivery && (
                        <PushButton primary onClick={handleReturn}>{t("ReturnDelivery")}</PushButton>
                    )}

                    {canCancel &&
                        <PushButton onClick={onCancel}>{t("Cancel")}</PushButton>
                    }
                </div>
            }
            strings={t}
        />
    );
}
